/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRecoilValue } from 'recoil';
import {
  RequestMethods,
  useFacialValidation,
  useRequest,
  useVariables,
  useLogger,
} from '../hooks';
import { useOperation } from '../providers';
import { endPoints, TypeDocumentBackend } from '../states';
import {
  newOperationIdState,
  operationIdState,
  countryState,
} from '../states/atoms';
import { getBase64Image, getCountryISO3 } from '../utils';

export const useWidgetOperations = () => {
  const { apiDemoUrl } = useVariables();
  const { requestBasic: demoRequest } = useRequest(apiDemoUrl);
  const { setAntispoofResult } = useOperation();

  const operationId = useRecoilValue(operationIdState);
  const newOperationId = useRecoilValue(newOperationIdState);
  const countryValue = useRecoilValue(countryState);

  const { onValidateSpoof } = useFacialValidation();
  const { captureException } = useLogger();

  const makeAntispoofing = async (
    encryptedLivenessRaw: Blob | File,
    session: string
  ) => {
    try {
      const responseAntispoof = await onValidateSpoof(
        encryptedLivenessRaw,
        operationId,
        session
      );

      setAntispoofResult(responseAntispoof);
    } catch (error: any) {
      captureException(error, { operation: 'makeAntispoofing' });
      throw new Error(error);
    }
  };

  const sendSelfie = async (operationId: string, image: any) => {
    try {
      const responseSelfie: any = await demoRequest(
        endPoints.Workflows.Assets,
        {
          method: RequestMethods.post,
          headers: {
            'Content-Type': 'application/json',
            'x-source': 'web',
            'x-operation-id': newOperationId,
          },
          data: {
            assetType: 'SELFIE',
            data: {
              selfie: image,
            },
          },
        }
      );

      return responseSelfie;
    } catch (e: any) {
      captureException(e, {
        operation: 'sendSelfie',
        url: endPoints.Workflows.Assets,
        operationId: newOperationId,
        image,
      });
      throw new Error(e);
    }
  };

  const sendDocument = async (
    operationId: string,
    front: string,
    back: string,
    country: string,
    documentType: TypeDocumentBackend
  ) => {
    try {
      const responseDocument: any = await demoRequest(
        endPoints.Workflows.Assets,
        {
          method: RequestMethods.post,
          headers: {
            'Content-Type': 'application/json',
            'x-source': 'web',
            'x-operation-id': newOperationId,
          },
          data: {
            assetType: 'DOCUMENT',
            data: {
              front: getBase64Image(front),
              back: !back ? undefined : getBase64Image(back),
              document: !getCountryISO3(countryValue)
                ? {
                    type: documentType,
                    country,
                  }
                : undefined,
            },
          },
        }
      );

      return responseDocument;
    } catch (e: any) {
      captureException(e, {
        operation: 'sendDocument',
        url: endPoints.Workflows.Assets,
        operationId: newOperationId,
        front,
        back,
      });
      throw new Error(e);
    }
  };

  return {
    makeAntispoofing,
    sendSelfie,
    sendDocument,
  };
};
